import { useEffect, useMemo, useState } from "react";
import { BUDGET, REPS_PER_SESSION } from "./config";
import { getSessionPrompts } from "./prompts";
import { evaluateResponse } from "./evaluate";
import { countWords, splitSentences } from "./scoring";
import {
  saveSession,
  getHistory,
  computeStreak,
  bestScore,
  backend,
} from "./storage";

export default function Trainer() {
  const [view, setView] = useState("home"); // home | loading | session | summary
  const [prompts, setPrompts] = useState([]);
  const [sessionResults, setSessionResults] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    getHistory().then(setHistory);
  }, []);

  const streak = useMemo(() => computeStreak(history), [history]);
  const best = useMemo(() => bestScore(history), [history]);

  async function start() {
    setView("loading");
    const p = await getSessionPrompts(REPS_PER_SESSION);
    setPrompts(p);
    setView("session");
  }

  async function finish(results) {
    const avg =
      results.reduce((s, r) => s + r.composite, 0) / (results.length || 1);
    await saveSession({
      avgScore: avg,
      reps: results.map((r) => ({
        prompt: r.prompt,
        shape: r.shapeId,
        score: r.composite,
        words: r.words,
        sentences: r.sentences,
        fillers: r.fillers.total,
      })),
    });
    setSessionResults(results);
    const fresh = await getHistory();
    setHistory(fresh);
    setView("summary");
  }

  return (
    <div className="at-app">
      <header className="at-top">
        <div className="at-brand">Articulation Trainer</div>
        <div className="at-streak" title="Consecutive days with a session">
          🔥 {streak.current}
        </div>
      </header>

      {view === "home" && (
        <Home streak={streak} best={best} onStart={start} />
      )}
      {view === "loading" && <Loading />}
      {view === "session" && (
        <Session prompts={prompts} onComplete={finish} />
      )}
      {view === "summary" && (
        <Summary
          results={sessionResults}
          streak={streak}
          onDone={() => setView("home")}
        />
      )}

      <footer className="at-foot">
        15 minutes · dictation only · {REPS_PER_SESSION} reps ·{" "}
        {backend === "supabase" ? "synced" : "saved on this device"}
      </footer>
    </div>
  );
}

function Home({ streak, best, onStart }) {
  return (
    <main className="at-main at-center">
      <div className="at-stats">
        <Stat label="Day streak" value={streak.current} />
        <Stat label="Best session" value={best || "—"} />
        <Stat
          label="Today"
          value={streak.playedToday ? "done ✅" : "not yet"}
        />
      </div>

      <p className="at-lede">
        Say your point first. One reason. One example. Then stop.
      </p>
      <p className="at-sub">
        {REPS_PER_SESSION} quick reps. Dictate each answer — don't type or paste.
        Score after every rep.
      </p>

      <button className="at-btn at-btn-lg" onClick={onStart}>
        {streak.playedToday ? "Practice again" : "Start today's session"}
      </button>
    </main>
  );
}

function Loading() {
  return (
    <main className="at-main at-center">
      <p className="at-lede">Building today's prompts…</p>
    </main>
  );
}

function Session({ prompts, onComplete }) {
  const [i, setI] = useState(0);
  const [results, setResults] = useState([]);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("answer"); // answer | scoring | feedback
  const [current, setCurrent] = useState(null);

  const p = prompts[i];
  const words = countWords(text);
  const sentences = splitSentences(text).length;
  const overWords = words > BUDGET.words;
  const overSentences = sentences > BUDGET.sentences;

  async function submit() {
    if (!text.trim()) return;
    setPhase("scoring");
    const result = await evaluateResponse({
      text,
      prompt: p.text,
      shape: p.shape,
    });
    const enriched = { ...result, prompt: p.text, shapeId: p.shape.id };
    setCurrent(enriched);
    setResults((r) => [...r, enriched]);
    setPhase("feedback");
  }

  function next() {
    const nextI = i + 1;
    if (nextI >= prompts.length) {
      onComplete(results);
      return;
    }
    setI(nextI);
    setText("");
    setCurrent(null);
    setPhase("answer");
  }

  if (!p) return null;

  return (
    <main className="at-main">
      <div className="at-rep-head">
        <span className="at-rep-count">
          Rep {i + 1} / {prompts.length}
        </span>
        <span className="at-shape">{p.shape.label}</span>
      </div>

      <div className="at-prompt">{p.text}</div>
      <div className="at-hint">{p.shape.hint}</div>

      {phase !== "feedback" && (
        <>
          <textarea
            className="at-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            placeholder="Dictate your answer (mic on your keyboard)…"
            rows={5}
            autoFocus
            disabled={phase === "scoring"}
          />
          <div className="at-meters">
            <Meter label="words" value={words} cap={BUDGET.words} over={overWords} />
            <Meter
              label="sentences"
              value={sentences}
              cap={BUDGET.sentences}
              over={overSentences}
            />
            <span className="at-nopaste">paste disabled</span>
          </div>
          <button
            className="at-btn"
            onClick={submit}
            disabled={!text.trim() || phase === "scoring"}
          >
            {phase === "scoring" ? "Scoring…" : "Score this rep"}
          </button>
        </>
      )}

      {phase === "feedback" && current && (
        <RepFeedback
          result={current}
          isLast={i + 1 >= prompts.length}
          onNext={next}
        />
      )}
    </main>
  );
}

function RepFeedback({ result, isLast, onNext }) {
  const m = result.metrics;
  return (
    <div className="at-feedback">
      <ScoreDial value={result.composite} />
      {m.onTarget === false && (
        <div className="at-offtarget">Off-target — that didn't answer the prompt.</div>
      )}
      <p className="at-fbline">{result.feedback}</p>
      <div className="at-bars">
        <Bar label="Economy" value={m.economy} />
        <Bar label="Filler-free" value={m.filler} />
        <Bar label="Point-first" value={m.point} />
        {m.tangents != null && <Bar label="On-track" value={m.tangents} />}
      </div>
      <button className="at-btn" onClick={onNext}>
        {isLast ? "See session summary" : "Next rep →"}
      </button>
    </div>
  );
}

function Summary({ results, streak, onDone }) {
  const avg = Math.round(
    results.reduce((s, r) => s + r.composite, 0) / (results.length || 1)
  );
  return (
    <main className="at-main">
      <h2 className="at-h2">Session done</h2>
      <div className="at-summary-top">
        <ScoreDial value={avg} big />
        <div className="at-streak-note">🔥 {streak.current}-day streak</div>
      </div>
      <ul className="at-rep-list">
        {results.map((r, idx) => (
          <li key={idx}>
            <span className="at-rep-score">{r.composite}</span>
            <span className="at-rep-prompt">{r.prompt}</span>
          </li>
        ))}
      </ul>
      <button className="at-btn at-btn-lg" onClick={onDone}>
        Done
      </button>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div className="at-stat">
      <div className="at-stat-value">{value}</div>
      <div className="at-stat-label">{label}</div>
    </div>
  );
}

function Meter({ label, value, cap, over }) {
  return (
    <span className={"at-meter" + (over ? " at-meter-over" : "")}>
      {value}/{cap} {label}
    </span>
  );
}

function Bar({ label, value }) {
  return (
    <div className="at-bar-row">
      <span className="at-bar-label">{label}</span>
      <span className="at-bar-track">
        <span className="at-bar-fill" style={{ width: `${value}%` }} />
      </span>
      <span className="at-bar-num">{value}</span>
    </div>
  );
}

function ScoreDial({ value, big }) {
  const color =
    value >= 80 ? "var(--good)" : value >= 55 ? "var(--mid)" : "var(--bad)";
  return (
    <div className={"at-dial" + (big ? " at-dial-big" : "")}>
      <span className="at-dial-num" style={{ color }}>
        {value}
      </span>
      <span className="at-dial-cap">/100</span>
    </div>
  );
}
